import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import {bzIconCalendarOutline} from '@bringoz/ui/icons';
import * as moment from 'moment';
import { BringozDateRange } from '../../models/bringoz-date-range.model';

require('./bringoz-date-range-picker.scss');
@Component({
  standalone: false,
  selector: 'app-bringoz-date-range-picker',
  templateUrl: './bringoz-date-range-picker.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class BringozDateRangePickerComponent implements OnInit, OnChanges {

  readonly icons = {calendar: bzIconCalendarOutline};
  @Input() selectedRange: BringozDateRange;
  @Input() min: any;
  @Input() max: any;
  @Input() customTemplates: BringozDateRange[] = [];
  @Input() isDisabled: boolean = false;
  @Input() configTimeZone: string;

  @Output() onChange = new EventEmitter<BringozDateRange>();

  public buttonDisplayName: string = '';
  private configTimeZoneOffset: number;
  private defaultSelection: any = {};
  private localizationValues: any;
  private dateFormat: any;

  constructor(
    @Inject('splitService') private splitService: any,
    @Inject('dateRangeTimeFiltersService') private dateRangeTimeFiltersService: any,
    @Inject('commonsDialogService') private commonsDialogService: any,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef
  ) {
    this.localizationValues = this.dateRangeTimeFiltersService.getLocalizationValues();
    this.dateFormat = this.splitService.getDateAndTimeConfig();
  }

  ngOnInit(): void {
    this.initializePicker();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedRange && !changes.selectedRange.isFirstChange()) {
      const currentStart = this.defaultSelection?.dateStart;
      const currentEnd = this.defaultSelection?.dateEnd;
      const updatedStart = this.selectedRange?.dateStart;
      const updatedEnd = this.selectedRange?.dateEnd;

      if (currentStart !== updatedStart || currentEnd !== updatedEnd) {
        if (this.selectedRange) {
          this.selectedRange = { ...this.selectedRange, name: undefined };
        }
        this.initializePicker();
      }
    }
  }

  private initializePicker(): void {
    if (!this.configTimeZone) {
      this.configTimeZone = this.splitService.getTimeZone();
    }

    this.configTimeZoneOffset = this.splitService.getTimeZoneOffsetByDate(this.configTimeZone, Date.now());

    if (!this.selectedRange) {
      this.selectedRange = Object.assign({}, this.dateRangeTimeFiltersService.today());
    }

    const customTemplatesCopy = (this.customTemplates || []).map(customTemplate => ({
      ...customTemplate,
      dateStart: this.getDate(customTemplate.dateStart),
      dateEnd: this.getDate(customTemplate.dateEnd)
    }));

    this.selectedRange = {
      ...this.selectedRange,
      name: this.getName(this.selectedRange.dateStart, this.selectedRange.dateEnd, this.selectedRange.name),
      dateStart: this.getDate(this.selectedRange.dateStart),
      dateEnd: this.getDate(this.selectedRange.dateEnd)
    };

    let selectedTemplate = this.getSelectedTemplate(
      customTemplatesCopy,
      this.selectedRange.dateStart,
      this.selectedRange.dateEnd,
      this.selectedRange.type
    );

    this.defaultSelection = {
      selectedTemplate: selectedTemplate ? selectedTemplate.name : this.selectedRange.name,
      selectedTemplateName: selectedTemplate ? selectedTemplate.name : this.selectedRange.name,
      dateStart: this.selectedRange.dateStart,
      dateEnd: this.selectedRange.dateEnd,
      min: this.min,
      max: this.max,
      showTemplate: true,
      customTemplates: customTemplatesCopy,
      fullscreen: true,
      firstDayOfWeek: 0,
      disableTemplates: "TD,YD,TW,LW,TY,LY,TM,LM",
      localizationMap: {}
    };

    this.buttonDisplayName = selectedTemplate ? selectedTemplate.name : (this.selectedRange.name || this.getName(
      this.selectedRange.dateStart,
      this.selectedRange.dateEnd,
      selectedTemplate?.name
    ));

    this.cdr.detectChanges();
  }

  openCalendar(event: any): void {
    // Note: To match AngularJS translations safely within the wrapper, we construct the object for the dialog
    Object.keys(this.localizationValues || {}).forEach(key => {
      this.defaultSelection.localizationMap[key] = this.localizationValues[key]; // Dialog will translate if needed
    });

    this.commonsDialogService.dateSelection(event, this.defaultSelection)
      .then((selectedDateRange: any) => {
        this.setSelectedRange(selectedDateRange);
      })
      .catch((err: any) => {
        console.error('Failed to filter by date', err);
      });
  }

  private setSelectedRange(selectedDateRange: any): void {
    if (selectedDateRange) {
      const selectedDateRangeCopy = { ...selectedDateRange };

      if (selectedDateRange.dateStart !== null && selectedDateRange.dateEnd !== null) {
        this.defaultSelection = selectedDateRange;
      }

      selectedDateRangeCopy.dateStart = this.splitService.getStartDayTimestamp(selectedDateRange.dateStart);
      selectedDateRangeCopy.dateEnd = this.splitService.getEndDayTimestamp(selectedDateRange.dateEnd);

      const customTemplatesCopy = (this.customTemplates || []).map(customTemplate => ({
        ...customTemplate,
        dateStart: this.getDate(customTemplate.dateStart),
        dateEnd: this.getDate(customTemplate.dateEnd)
      }));

      let selectedTemplate = this.getSelectedTemplate(
        customTemplatesCopy,
        selectedDateRangeCopy.dateStart,
        selectedDateRangeCopy.dateEnd,
        selectedDateRangeCopy.type,
      );

      this.buttonDisplayName = this.getName(
        selectedDateRangeCopy.dateStart,
        selectedDateRangeCopy.dateEnd,
        selectedTemplate?.name
      );

      let range = {
        name: this.defaultSelection.selectedTemplateName,
        type: selectedTemplate?.type,
        dateStart: selectedDateRangeCopy.dateStart,
        dateEnd: selectedDateRangeCopy.dateEnd
      };

      this.onChange.emit(range);
      this.cdr.detectChanges();
    }
  }

  private getName(dateStart: any, dateEnd: any, name?: string): string {
    if (name) {
      return name; // We allow pipe 'translate' in template to handle string mapping
    }
    return this.dateRangeTimeFiltersService.getRangeDisplay(dateStart, dateEnd, this.dateFormat);
  }

  private getSelectedTemplate(templates: any[], dateStart: any, dateEnd: any, type?: string): any {
    return (templates || []).find((t) => {
      return (!!type && t.type === type) || (moment(t.dateStart).valueOf() === moment(dateStart).valueOf() &&
        moment(t.dateEnd).valueOf() === moment(dateEnd).valueOf());
    });
  }

  private getDate(date: any): Date | undefined {
    return this.configTimeZone && date ?
      new Date(moment.tz(date, this.configTimeZone).utcOffset(-(this.configTimeZoneOffset)).toISOString()) :
      undefined;
  }
}
